import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';

/**
 * Global top-bar progress loader.
 *
 * Renders a thin animated bar fixed to the very top of the viewport. It kicks
 * off on every route change (driven by useLocation) and completes shortly
 * after the new page renders — giving users feedback while lazy-loaded views
 * (React.lazy + Suspense) resolve.
 */
const TopBarLoader: React.FC = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Skip the loader on the very first mount (initial page load).
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Clear any in-flight timers from a previous navigation.
    timers.current.forEach(clearTimeout);
    timers.current = [];

    setVisible(true);
    setProgress(15);

    // Ramp the bar up so it feels responsive without ever hitting 100%
    // until the page is ready.
    timers.current.push(setTimeout(() => setProgress(45), 80));
    timers.current.push(setTimeout(() => setProgress(70), 220));
    timers.current.push(setTimeout(() => setProgress(88), 450));

    // Finish: snap to 100%, then fade out and reset.
    timers.current.push(setTimeout(() => setProgress(100), 600));
    timers.current.push(setTimeout(() => setVisible(false), 850));
    timers.current.push(setTimeout(() => setProgress(0), 1050));

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };
  }, [location.pathname, location.search]);

  if (!visible && progress === 0) return null;

  return (
    <div className="topbar-loader" aria-hidden="true">
      <div
        className="topbar-loader-bar"
        style={{
          width: `${progress}%`,
          opacity: visible ? 1 : 0,
        }}
      />
    </div>
  );
};

export default TopBarLoader;
